import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Divider, useMediaQuery, useTheme,
  Chip, Menu, MenuItem, Badge, Popover, Stack, Button,
} from '@mui/material';
import {
  Dashboard, Home as HomeIcon, Bookmark, Business, Payment, SupportAgent,
  Person, SmartToy, Group, Menu as MenuIcon, ChevronLeft, Logout,
  AdminPanelSettings, VerifiedUser, ManageAccounts, ConfirmationNumber,
  Notifications as NotificationsIcon, Description, DoneAll, Chat,
  Settings, Sms, Psychology, Gavel, TrendingUp,
} from '@mui/icons-material';
import useAuthStore from '../store/useStore';
import { notificationsAPI, chatAPI } from '../api/client';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
const DRAWER_WIDTH = 260;

const tenantMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Messages', icon: <Chat />, path: '/dashboard/chat' },
  { type: 'divider' },
  { text: 'Browse Properties', icon: <HomeIcon />, path: '/dashboard/browse' },
  { text: 'My Bookings', icon: <Bookmark />, path: '/dashboard/bookings' },
  { text: 'Payments', icon: <Payment />, path: '/dashboard/payments' },
  { text: 'Room Pairing', icon: <Group />, path: '/dashboard/room-pairing' },
  { text: 'Tenancy', icon: <Description />, path: '/dashboard/tenancy' },
  { type: 'divider' },
  { text: 'Support Tickets', icon: <SupportAgent />, path: '/dashboard/tickets' },
  { text: 'Disputes', icon: <Gavel />, path: '/dashboard/disputes' },
  { text: 'AI Assistant', icon: <SmartToy />, path: '/dashboard/ai-assistant' },
  { text: 'Profile', icon: <Person />, path: '/dashboard/profile' },
];

const landlordMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Messages', icon: <Chat />, path: '/dashboard/chat' },
  { type: 'divider' },
  { text: 'My Properties', icon: <Business />, path: '/dashboard/properties' },
  { text: 'Bookings', icon: <Bookmark />, path: '/dashboard/bookings' },
  { text: 'Payments', icon: <Payment />, path: '/dashboard/payments' },
  { text: 'Revenue Analytics', icon: <TrendingUp />, path: '/dashboard/revenue' },
  { text: 'Tenancy', icon: <Description />, path: '/dashboard/tenancy' },
  { type: 'divider' },
  { text: 'Support Tickets', icon: <SupportAgent />, path: '/dashboard/tickets' },
  { text: 'Disputes', icon: <Gavel />, path: '/dashboard/disputes' },
  { text: 'Profile', icon: <Person />, path: '/dashboard/profile' },
];

const adminMenuItems = [
  { text: 'Dashboard', icon: <AdminPanelSettings />, path: '/admin' },
  { text: 'Messages', icon: <Chat />, path: '/dashboard/chat' },
  { type: 'divider', label: 'User Management' },
  { text: 'Users', icon: <ManageAccounts />, path: '/admin/users' },
  { text: 'KYC Verification', icon: <VerifiedUser />, path: '/admin/kyc' },
  { type: 'divider', label: 'Property & Booking' },
  { text: 'Properties', icon: <Business />, path: '/admin/properties' },
  { text: 'Bookings', icon: <Bookmark />, path: '/admin/bookings' },
  { text: 'Room Pairing', icon: <Group />, path: '/admin/room-pairing' },
  { text: 'Tenancy', icon: <Description />, path: '/admin/tenancy' },
  { type: 'divider', label: 'Support & Finance' },
  { text: 'Tickets', icon: <ConfirmationNumber />, path: '/admin/tickets' },
  { text: 'Disputes', icon: <Gavel />, path: '/admin/disputes' },
  { text: 'Payments', icon: <Payment />, path: '/admin/payments' },
  { type: 'divider', label: 'Settings' },
  { text: 'AI Assistant', icon: <Psychology />, path: '/admin/settings/ai' },
  { text: 'SMS Settings', icon: <Sms />, path: '/admin/settings/sms' },
  { text: 'Chat Management', icon: <Gavel />, path: '/admin/chat' },
  { text: 'System Settings', icon: <Settings />, path: '/admin/settings/system' },
  { type: 'divider' },
  { text: 'Profile', icon: <Person />, path: '/dashboard/profile' },
];

const roleColors = {
  super_admin: 'error',
  landlord: 'primary',
  agent: 'secondary',
  tenant: 'success',
  rent_control: 'warning',
};

function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadCount(res.data.unread_count);
      } catch (_) {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  // Heartbeat for online status
  useEffect(() => {
    chatAPI.heartbeat().catch(() => {});
    const hb = setInterval(() => chatAPI.heartbeat().catch(() => {}), 30000);
    const handleBeforeUnload = () => chatAPI.goOffline().catch(() => {});
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      clearInterval(hb);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      chatAPI.goOffline().catch(() => {});
    };
  }, []);

  const getNotificationRoute = (n) => {
    const st = n.source_type;
    const sid = n.source_id;
    if (!st) return null;
    switch (st) {
      case 'chat': return `/dashboard/chat?conversation=${sid}`;
      case 'booking': return `/dashboard/bookings`;
      case 'payment': return `/dashboard/payments`;
      case 'ticket': return `/dashboard/tickets`;
      case 'property': return `/dashboard/properties`;
      case 'tenancy': return `/dashboard/tenancy`;
      case 'kyc': return `/dashboard/profile`;
      case 'profile': return `/dashboard/profile`;
      default: return null;
    }
  };

  const handleNotificationClick = async (n) => {
    try {
      if (!n.is_read) {
        await notificationsAPI.markAsRead(n.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (_) {}
    const route = getNotificationRoute(n);
    if (route) {
      setNotifAnchor(null);
      navigate(route);
    }
  };

  const handleNotifOpen = async (e) => {
    setNotifAnchor(e.currentTarget);
    try {
      const res = await notificationsAPI.list({ page: 1, per_page: 5 });
      setNotifications(res.data.notifications || []);
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (_) {}
  };

  const getMenuItems = () => {
    if (!user) return tenantMenuItems;
    switch (user.role) {
      case 'super_admin':
      case 'rent_control':
        return adminMenuItems;
      case 'landlord':
      case 'agent':
        return landlordMenuItems;
      default:
        return tenantMenuItems;
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" fontWeight={700} color="primary" component={Link} to="/" sx={{ textDecoration: 'none' }}>
          CampusNest
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} sx={{ ml: 'auto' }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>
      <Divider />

      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          src={user?.avatar_url ? `${API_BASE}${user.avatar_url}` : undefined}
          sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}
        >
          {!user?.avatar_url && <>{user?.first_name?.[0]}{user?.last_name?.[0]}</>}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{user?.first_name} {user?.last_name}</Typography>
          <Chip label={user?.role?.replace('_', ' ')} size="small" color={roleColors[user?.role] || 'default'} sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }} />
        </Box>
      </Box>
      <Divider />

      <List sx={{ flexGrow: 1, px: 1 }}>
        {getMenuItems().map((item, index) =>
          item.type === 'divider' ? (
            <Box key={index}>
              <Divider sx={{ my: 1 }} />
              {item.label && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {item.label}
                </Typography>
              )}
            </Box>
          ) : (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                onClick={() => isMobile && setMobileOpen(false)}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } },
                  '&.Mui-selected:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </ListItem>
          )
        )}
      </List>

      <Divider />
      <List sx={{ px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { logout(); navigate('/'); }} sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {isMobile ? (
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: '1px solid #e0e0e0' } }}>
          {drawerContent}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {isMobile ? (
          <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Toolbar>
              <IconButton onClick={() => setMobileOpen(true)} edge="start"><MenuIcon /></IconButton>
              <Typography variant="h6" fontWeight={600} color="primary" sx={{ ml: 1, flexGrow: 1 }}>CampusNest</Typography>
              <IconButton onClick={handleNotifOpen}>
                <Badge badgeContent={unreadCount} color="error"><NotificationsIcon /></Badge>
              </IconButton>
            </Toolbar>
          </AppBar>
        ) : (
          <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Toolbar sx={{ justifyContent: 'flex-end' }}>
              <IconButton onClick={handleNotifOpen} sx={{ mr: 1 }}>
                <Badge badgeContent={unreadCount} color="error"><NotificationsIcon /></Badge>
              </IconButton>
              <Avatar
                src={user?.avatar_url ? `${API_BASE}${user.avatar_url}` : undefined}
                sx={{ bgcolor: 'primary.main', width: 32, height: 32, mr: 1, fontSize: '0.85rem' }}
              >
                {!user?.avatar_url && <>{user?.first_name?.[0]}{user?.last_name?.[0]}</>}
              </Avatar>
              <Typography variant="body2" color="text.secondary">{user?.first_name} {user?.last_name}</Typography>
            </Toolbar>
          </AppBar>
        )}

        <Popover
          open={Boolean(notifAnchor)}
          anchorEl={notifAnchor}
          onClose={() => setNotifAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" startIcon={<DoneAll />} onClick={handleMarkAllRead}>Mark all read</Button>
            )}
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            <List dense sx={{ p: 0 }}>
              {notifications.map((n) => (
                <ListItem
                  key={n.id}
                  sx={{ bgcolor: n.is_read ? 'transparent' : 'action.hover', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                  onClick={() => handleNotificationClick(n)}
                >
                  <ListItemText
                    primary={n.title}
                    secondary={n.message}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: n.is_read ? 400 : 600 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Popover>

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default DashboardLayout;
